import ArrowBackRoundedIcon from "@mui/icons-material/ArrowBackRounded";
import SettingsIcon from "@mui/icons-material/SettingsOutlined";
import {
  Box,
  Button,
  Divider,
  FormControlLabel,
  FormHelperText,
  IconButton,
  MenuItem,
  Paper,
  Select,
  Stack,
  Switch,
  Typography,
} from "@mui/material";
import { useMemo, useState } from "react";
import { Link } from "react-router";
import { withErrorHandling } from "@entrilia/shared/api/axiosHelper";
import type { PortalWidget, PortalWidgetSettings } from "@entrilia/shared/api/portalWidgetsTypes";
import { useNotificationContext } from "@entrilia/shared/contexts/NotificationContext";
import { logError } from "@entrilia/shared/logging";
import adminApi from "../../../../api/adminApi";
import type { ReportInfo } from "../../../../api/types/svTypes";
import useClientContext from "../../../../context/useClientContext";
import EditableTitle from "../../../common/EditableTitle";
import {
  getEditSystemReportUrl,
  getViewSystemReportUrl,
} from "../../reporting/report-lists/reports/utilities/editReportUrl";
import type { PortalPageStatus } from "../pages/portalPageStatusHelper";
import PortalWidgetAvatar from "./PortalWidgetAvatar";
import PortalWidgetFootnoteSection from "./PortalWidgetFootnoteSection";
import {
  getInitialEditPortalWidgetState,
  isEditPortalWidgetStateDirty,
  mapStateToUpdatePortalWidgetRequest,
  validateEditPortalWidgetState,
} from "./portalWidgetSettingsState";
import { getAvailableWidgetStatuses, getWidgetDataSourceType } from "./portalWidgetsHelper";
import PortalWidgetStatusSelect from "./PortalWidgetStatusSelect";

const ReadonlyDataSource = ({ text }: { text: string }) => (
  <Paper variant="outlined" sx={{ p: 1.5, bgcolor: (t) => t.palette.background.grey }}>
    <Typography variant="subtitle2">{text}</Typography>
  </Paper>
);

interface Props {
  widget: PortalWidget;
  pageStatus: PortalPageStatus;
  systemReports: ReportInfo[];
  hasReportsViewPermissions: boolean;
  hasReportsEditPermissions: boolean;
  onWidgetUpdated: (updatedSettings: PortalWidgetSettings) => void;
  onDeselectWidget: () => void;
}

const updatePortalWidget = withErrorHandling(adminApi.updatePortalWidget);

const PortalWidgetEditor = ({
  widget,
  pageStatus,
  systemReports,
  hasReportsViewPermissions,
  hasReportsEditPermissions,
  onWidgetUpdated,
  onDeselectWidget,
}: Props) => {
  const { clientCode } = useClientContext();
  const { sendNotification, sendNotificationError } = useNotificationContext();

  const [editedWidget, setEditedWidget] = useState(() => getInitialEditPortalWidgetState(widget));
  const [saving, setSaving] = useState(false);

  const reportLink = useMemo(() => {
    if (!editedWidget.reportId) {
      return undefined;
    }
    if (hasReportsEditPermissions) {
      return getEditSystemReportUrl(clientCode, editedWidget.reportId);
    }
    if (hasReportsViewPermissions) {
      return getViewSystemReportUrl(clientCode, editedWidget.reportId);
    }
    return undefined;
  }, [clientCode, hasReportsEditPermissions, hasReportsViewPermissions, editedWidget.reportId]);

  const [isValid, validationErrors] = useMemo(() => validateEditPortalWidgetState(editedWidget), [editedWidget]);

  const isDirty = useMemo(() => isEditPortalWidgetStateDirty(editedWidget, widget), [editedWidget, widget]);

  const handleReset = () => {
    setEditedWidget(getInitialEditPortalWidgetState(widget));
  };

  const handleSave = async () => {
    if (!isValid) {
      return;
    }

    setSaving(true);
    const payload = mapStateToUpdatePortalWidgetRequest(editedWidget, widget);
    const [updatedWidgetSettings, error] = await updatePortalWidget(widget.id, payload);
    setSaving(false);

    if (error) {
      logError(error, "[PortalWidgetSettings] updatePortalWidget");
      sendNotificationError(error.message);
      return;
    }

    sendNotification("Widget updated successfully.");

    const updatedWidget = updatedWidgetSettings.widgets.find((w) => w.id === widget.id);
    if (updatedWidget) {
      setEditedWidget(getInitialEditPortalWidgetState(updatedWidget));
    }

    onWidgetUpdated(updatedWidgetSettings);
  };

  const widgetDataSourceType = getWidgetDataSourceType(widget);

  const availableStatuses = getAvailableWidgetStatuses(pageStatus);

  return (
    <Box sx={{ width: "100%", display: "flex", flexDirection: "column", height: "100%" }}>
      <Stack direction="row" spacing={1} sx={{ alignItems: "center", px: 1.5, py: 1.5, width: "100%" }}>
        <IconButton onClick={onDeselectWidget}>
          <ArrowBackRoundedIcon />
        </IconButton>
        <PortalWidgetAvatar predefinedWidgetType={widget.predefinedType} />
        <Box sx={{ flex: "1 1 auto", minWidth: 0 }}>
          <EditableTitle
            title={editedWidget.name}
            onTitleChanged={(name) => setEditedWidget((prev) => ({ ...prev, name }))}
            tooltip="Rename widget"
            placeholder="Enter widget name"
          />
        </Box>
      </Stack>
      <Divider />
      <Box sx={{ p: 2, width: "100%", flex: "1 1 auto" }}>
        <Stack direction="row" spacing={2} sx={{ alignItems: "center", mb: 2 }}>
          <Typography variant="subtitle1">Widget Status</Typography>
          <PortalWidgetStatusSelect
            value={editedWidget.status}
            availableStatuses={availableStatuses}
            disabled={widgetDataSourceType === "SystemReport" && !hasReportsViewPermissions}
            onChange={(status) => setEditedWidget((prev) => ({ ...prev, status }))}
          />
        </Stack>

        <Divider />

        <Stack spacing={1} sx={{ py: 2.5 }}>
          <Typography variant="subtitle1" sx={{ pb: 1 }}>
            Data Source
          </Typography>

          {widgetDataSourceType === "CashFlowTransactions" && <ReadonlyDataSource text="Cashflow Transactions" />}

          {widgetDataSourceType === "InvestorCommunications" && <ReadonlyDataSource text="Investor Communications" />}

          {widgetDataSourceType === "SystemReport" && (
            <Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
              <Select
                sx={{ flex: "1 1 auto", minWidth: 0 }}
                disabled={!hasReportsViewPermissions}
                value={editedWidget.reportId ?? null}
                onChange={(e) => setEditedWidget((prev) => ({ ...prev, reportId: e.target.value || undefined }))}
                error={!!validationErrors.reportId}
              >
                {systemReports.map((report) => (
                  <MenuItem key={report.reportId} value={report.reportId}>
                    {report.name}
                  </MenuItem>
                ))}
              </Select>

              {reportLink && (
                <Button
                  component={Link}
                  to={reportLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  variant="outlined"
                  color="secondary"
                  startIcon={<SettingsIcon />}
                  sx={{ minWidth: "auto", flexShrink: 0 }}
                >
                  Settings
                </Button>
              )}
            </Stack>
          )}

          {validationErrors.reportId && <FormHelperText error>{validationErrors.reportId}</FormHelperText>}
        </Stack>

        {widget.predefinedType !== "News" && (
          <>
            <Divider />

            <PortalWidgetFootnoteSection
              footnote={editedWidget.footnote}
              isShown={editedWidget.isFootnoteShown}
              onFootnoteChange={(footnote) => setEditedWidget((prev) => ({ ...prev, footnote }))}
              onIsShownChange={(isFootnoteShown) => setEditedWidget((prev) => ({ ...prev, isFootnoteShown }))}
            />
          </>
        )}

        {widget.predefinedType === "CashFlows" && (
          <>
            <Divider />

            <Stack spacing={1} sx={{ py: 2.5 }}>
              <Typography variant="subtitle1" sx={{ pb: 1 }}>
                Layout
              </Typography>
              <FormControlLabel
                control={
                  <Switch
                    checked={editedWidget.includeRowDetails ?? false}
                    onChange={(_, checked) => setEditedWidget((prev) => ({ ...prev, includeRowDetails: checked }))}
                  />
                }
                label="Show Cash Flow Details"
                labelPlacement="start"
                sx={{ justifyContent: "space-between" }}
              />
            </Stack>
          </>
        )}
      </Box>
      <Divider />
      <Stack direction="row" spacing={1} sx={{ alignItems: "center", px: 3, py: 2 }}>
        <Button
          variant="contained"
          color="primary"
          onClick={handleSave}
          disabled={!isValid || !isDirty}
          loading={saving}
        >
          Save
        </Button>
        <Button variant="text" color="secondary" onClick={handleReset} disabled={!isDirty || saving}>
          Cancel
        </Button>
      </Stack>
    </Box>
  );
};

export default PortalWidgetEditor;
